import { useMemo } from 'react';
import { usePermissions } from 'permissions';
import { useDebounce } from 'common/hooks';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetNodeCommunities } from 'features/communities/queries';
import { useGetMediaSourceVariant } from 'features/media';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetNodeUserAvatar } from 'features/users/hooks/useGetNodeUserAvatar';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetNodeUsers } from 'features/users/queries';

const MAX_SEARCH_RESULTS = 10;
const PREFFERED_GROUP_RESULTS_SIZE = MAX_SEARCH_RESULTS / 2;

type P = {
  search?: string;
};

export const useInvitationSearchOptions = ({ search }: P) => {
  const {
    nodeUserData: { _id },
  } = useUser();
  const getNodeUserAvatar = useGetNodeUserAvatar();
  const getMediaSource = useGetMediaSourceVariant();
  const { canInviteCommunities } = usePermissions();
  const { isDebouncing, value } = useDebounce(search);

  const { data: users, isLoading: isLoadingUsers } = useGetNodeUsers({
    filter: { $search: value, scope: 'web' },
    pagination: { pageSize: MAX_SEARCH_RESULTS },
  });

  const { data: communities, isLoading: isLoadingCommunities } = useGetNodeCommunities({
    filter: { admin: _id, search: value },
    options: { enabled: canInviteCommunities },
    pagination: { pageSize: MAX_SEARCH_RESULTS },
  });

  const options = useMemo(() => {
    const userOptions =
      users?.data.map((user) => ({
        email: user.email,
        label: `${user.firstName} ${user.lastName}`,
        profilePhoto: getNodeUserAvatar({ userId: user._id }),
        type: 'user' as const,
        value: user._id,
      })) ?? [];

    const communityOptions =
      communities?.data.map((community) => ({
        label: community.name,
        membersCount: community.membersCount,
        profilePhoto: getMediaSource({ id: community.id, type: 'community_profile_photo' }),
        type: 'community' as const,
        value: community.id,
      })) ?? [];

    // Prefer an even split between users and communities, then use leftover capacity for whichever group has more matches.
    const prefferedUserCount = Math.min(PREFFERED_GROUP_RESULTS_SIZE, userOptions.length);
    const prefferedCommunityCount = Math.min(PREFFERED_GROUP_RESULTS_SIZE, communityOptions.length);
    const remainingSlots = MAX_SEARCH_RESULTS - prefferedUserCount - prefferedCommunityCount;
    const extraUserCount = Math.min(remainingSlots, userOptions.length - prefferedUserCount);
    const extraCommunityCount = Math.min(
      remainingSlots - extraUserCount,
      communityOptions.length - prefferedCommunityCount,
    );

    return [
      ...userOptions.slice(0, prefferedUserCount + extraUserCount),
      ...communityOptions.slice(0, prefferedCommunityCount + extraCommunityCount),
    ].slice(0, MAX_SEARCH_RESULTS);
  }, [users?.data, communities?.data, getNodeUserAvatar, getMediaSource]);

  return useMemo(
    () => ({ isLoading: isLoadingUsers || isLoadingCommunities || isDebouncing, options }),
    [isLoadingUsers, isLoadingCommunities, isDebouncing, options],
  );
};
